import type { ReactNode } from 'react'

/**
 * One block of a long form: a heading, a line saying what the block is for, and at most one action
 * belonging to it.
 *
 * The capture dialog is the longest screen in the app, and a wall of inputs separated only by
 * rules gives the eye nothing to hold on to. The heading rule is what makes the blocks legible
 * while scrolling; the description is what keeps the individual fields from having to explain
 * themselves one by one.
 *
 * Shared rather than lead-specific: the customer detail is built from the same blocks, and two
 * dialogs that look alike is the point.
 */
export function FormSection({
  title,
  description,
  action,
  children,
}: {
  title: string
  description?: string
  /** The one thing this block can do, top right — "find a customer", "add a contact". */
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1 border-b pb-2">
        <div className="grid gap-0.5">
          <h3 className="text-sm leading-tight font-semibold">{title}</h3>
          {description && <p className="text-muted-foreground text-xs">{description}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </section>
  )
}

/**
 * The one grid every block uses. Two columns on anything but a phone, the same gap everywhere, so
 * a field edge in one block lines up with the field edge in the next one — which is the whole
 * reason the form does not mix a three-column row in wherever three fields happened to fit.
 */
export function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2">{children}</div>
}

/** A field that takes the full width of [FieldGrid] rather than half of it. */
export function FullWidth({ children }: { children: ReactNode }) {
  return <div className="sm:col-span-2">{children}</div>
}
